import { Component } from "@outschool/ownership-areas";

export { osEnvironment } from "./lib/implementation";

/**
 * @see OsEnvironment.envType
 */
export type OsEnvType = "prod" | "stage" | "review" | "dev" | "test";
/**
 * @see OsEnvironment.workerType
 */
export type OsWorkerType = "server" | "job" | "browser" | "unknown";

export type OsEnvironment = {
  /**
   * This reflects the location of where the project is currently running. This is
   * a useful property for all sorts of production/not-production/test logic.
   */
  readonly envType: OsEnvType;
  /**
   * A more human-readable name of the current environment. Of particular note, in
   * review this will match the environment name used for configuring infrastructure.
   * You should prefer using {@link envType} over this for production/test logic,
   * but this is a useful property still when you need to inject a name into something
   * like an S3 path.
   */
  readonly appEnv: "Prod" | "Stage" | string | "Dev" | "Test";
  /**
   * The current type of worker that is running, be it a server or job or the browser.
   */
  readonly workerType: OsWorkerType;
  /**
   * This name matches the directory of the current running app in lang-js.
   * Note that this does not have a useful value in dev or test.
   * This value is useful for logging and debugging purposes.
   */
  readonly appName: string;
  /**
   * This name matches the name of the worker in the infra.ts file.
   * Note that this does not have a useful value in dev or test.
   * This value is useful for logging and debugging purposes.
   */
  readonly appWorker: string;
  /**
   * This property matches the configured alarm component in the infra.ts file
   * for the current worker. It may be undefined if there is none there (and will
   * certainly be undefined in dev and test).
   */
  readonly alarmComponent: Component | undefined;
  /**
   * This property matches the git commit hash of the repo at build time.
   * It does not have a useful dev or test value.
   */
  readonly gitCommit: string;
  /**
   * This property combines most of the env values into a single string
   * that can be passed along to other services to indicate who called it.
   */
  readonly osUserAgent: string;
  /**
   * Optional property to add known http outgoing requests that we don't care about tracking
   * via the env var OS_PLATFORM_HTTP_DENY_LIST in a comma seperated string.
   */
  readonly httpDenyList?: string[];
};
